import strings from '../../strings/strings.json';
import { breakpoints, sizes, theme } from 'theme';
import { PublishedCourse } from 'models/Course';
import ModuleAccordion from 'pages/build-course/components/ModuleAccordion';
import MainSection from 'components/sections/MainSection';
import styled from 'styled-components';
import {
    BodyRegular,
    FlexAlignCenter,
    FlexMdAlignCenter,
    FlexColMd,
    Headline3,
    FlexColRg,
    HorizontalRule,
} from '../Elements';
import { useNavigate } from 'react-router-dom';
import { LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import CheckIcon from 'assets/icons/form-icons/CheckIcon';
import ScheduleIcon from 'assets/icons/navigation/ScheduleIcon';
import VisibilityIcon from 'assets/icons/accessibility/VisibilityIcon';
import EditIcon from 'assets/icons/controls/EditIcon';
import useDeleteRequest from 'hooks/useDeleteRequest';
import React from 'react';
import Alert from 'components/alert/Alert';
import { logError } from 'lib/debug-helpers';
import { AuthContext } from 'contextProviders/AuthContext';
import { RoleType } from 'models/User';
import PaymentSetupModal from 'pages/OA/components/PaymentSetupModal';
import {
    FirstCourseForFreeNotifier,
    NoPaymentNotifierForTrainers,
    ScheduleCourseChargeNotifier,
    ScheduleCoursePaymentSetupNotifier,
} from 'components/notifiers/PaymentNotifiers';
import useModifyRequest from 'hooks/useModifyRequest';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import Button from 'components/buttons/Button';

type PublishedCourseViewProps = {
    course: PublishedCourse;
};

export default function PublishedCourseView({ course }: PublishedCourseViewProps): JSX.Element {
    const { userData, refreshUserData } = React.useContext(AuthContext);

    const [openDeleteCourseAreYouSure, setOpenDeleteCourseAreYouSure] = React.useState<boolean>(false);
    const [deleteCourseError, setDeleteCourseError] = React.useState<boolean>(false);

    const [openCopyCourseModal, setOpenCopyCourseModal] = React.useState<boolean>(false);
    const [copyCourseError, setCopyCourseError] = React.useState<boolean>(false);

    const [openSetupPaymentModal, setOpenPaymentModal] = React.useState<boolean>(false);

    const [paymentSetupRequired, setPaymentSetupRequired] = React.useState<boolean>(
        !course.courseIsFree && !userData.organisation?.hasValidPaymentMethod,
    );

    const { deleteData: deleteCourse, deleteInProgress } = useDeleteRequest(`courses/${course.id}`);
    const { modifyData: copyCourse, loading: copyingCourse } = useModifyRequest<any, string>(
        `courses/${course.id}/copy`,
        'POST',
    );

    React.useEffect(() => {
        const fetchData = async () => {
            await refreshUserData();
        };
        fetchData();
    }, []); //eslint-disable-line

    const navigate = useNavigate();

    const previewCourse = () => {
        navigate(`/preview/${course.id}`, {
            state: {
                previewContextEntryPoint: `/published-course/${course.id}`,
            },
        });
    };

    const editCourse = () => {
        navigate(`/published-course/${course.id}/edit`);
    };

    async function onDelete() {
        const response = await deleteCourse();
        if (response.errors) {
            logError(response.errors);
            setDeleteCourseError(true);
            return;
        }
        setOpenDeleteCourseAreYouSure(false);
        setDeleteCourseError(false);
        navigate('/organisation/dashboard');
    }

    async function onCopy() {
        const response = await copyCourse({});
        if (response.errors) {
            logError(response.errors);
            setCopyCourseError(true);
            return;
        }
        setOpenCopyCourseModal(false);
        setCopyCourseError(false);

        const path = userIsOrgAdmin ? '/organisation/dashboard' : '/trainer/dashboard';
        const state = userIsOrgAdmin
            ? { activeTabId: 'courses', activeRowId: response.value }
            : { activeTabId: 'buildCourses', activeRowId: response.value };

        navigate(path, { state });
    }

    async function closePaymentModal() {
        setOpenPaymentModal(false);
    }

    async function closePaymentModalAfterAddCard() {
        setOpenPaymentModal(false);
        setPaymentSetupRequired(false);
        await refreshUserData();
    }

    const userIsTrainer = userData.activeRole === RoleType.TRAINER;
    const userIsOrgAdmin = userData.activeRole === RoleType.ORGANISATION_ADMIN;
    const organisationHasNoScheduledCourses = course.organisationNumberOfLiveCourses === 0;

    function CopyButton() {
        return (
            <Button
                title={strings.courseOverview.copyButton.title}
                aria={strings.courseOverview.copyButton.aria}
                onClick={() => setOpenCopyCourseModal(true)}
            />
        );
    }

    function PreviewButton() {
        return (
            <Button
                title={strings.courseOverview.previewButton.title}
                aria={strings.courseOverview.previewButton.aria}
                icon={<VisibilityIcon colour={theme.primaryBrandColour} />}
                onClick={previewCourse}
            />
        );
    }

    function EditButton() {
        return (
            <Button
                title={strings.courseOverview.editButton.title}
                aria={strings.courseOverview.editButton.aria}
                icon={<EditIcon />}
                onClick={editCourse}
            />
        );
    }

    function DeleteButton() {
        return (
            <Button
                title={strings.courseOverview.deleteButton.title}
                aria={strings.courseOverview.deleteButton.aria}
                colour={'BLACK'}
                onClick={() => {
                    setOpenDeleteCourseAreYouSure(true);
                }}
            />
        );
    }

    function ScheduleButton() {
        return (
            <Button
                title={strings.courseOverview.scheduleButton.title}
                aria={strings.courseOverview.scheduleButton.aria}
                colour="PRIMARY"
                icon={<ScheduleIcon colour={theme.primaryButtonTextColour} />}
                disabled={paymentSetupRequired}
                onClick={() => {
                    navigate(`/published-course/${course.id}/schedule`, {
                        state: {
                            backUrl: `/published-course/${course.id}`,
                        },
                    });
                }}
            />
        );
    }

    function getViewState() {
        if (organisationHasNoScheduledCourses) return 'firstCourseFree';
        if (paymentSetupRequired && userIsTrainer) return 'noPaymentTrainer';
        if (paymentSetupRequired && userIsOrgAdmin) return 'noPaymentOrgAdmin';
        if (userData.organisation?.hasValidPaymentMethod && !course.courseIsFree) return 'chargeNotifier';
        return 'default';
    }

    const viewState = getViewState();
    const courseIsLive = course.courseSchedulesCount;

    function CourseButtons() {
        return (
            <CourseButtonsContainer>
                <CopyButton />
                <PreviewButton />
                {!courseIsLive && <EditButton />}
                {!courseIsLive && <DeleteButton />}
            </CourseButtonsContainer>
        );
    }

    function CourseIsLiveEditWarning() {
        if (course.courseSchedulesCount > 0)
            return (
                <Notification
                    notificationType={NotificationType.WARNING}
                    title={strings.publishedCoursePage.editPublishedCourseWarning.title}
                    button={<EditButton />}
                    dataTestId="notification-edit-live-course-warning"
                />
            );
        return null;
    }

    function ButtonPanel() {
        switch (viewState) {
            case 'firstCourseFree':
                return (
                    <>
                        <FirstCourseForFreeNotifier button={<ScheduleButton />} />
                        <CourseIsLiveEditWarning />
                        <CourseButtons />
                    </>
                );
            case 'noPaymentTrainer':
                return (
                    <>
                        <NoPaymentNotifierForTrainers />
                        <CourseIsLiveEditWarning />
                        <CourseButtons />
                    </>
                );
            case 'noPaymentOrgAdmin':
                return (
                    <>
                        <ScheduleCoursePaymentSetupNotifier action={() => setOpenPaymentModal(true)} />
                        <CourseIsLiveEditWarning />
                        <CourseButtons />
                    </>
                );
            case 'chargeNotifier':
                return (
                    <>
                        <ScheduleCourseChargeNotifier
                            organisationIsSubscribed={!paymentSetupRequired}
                            button={<ScheduleButton />}
                        />
                        <CourseIsLiveEditWarning />
                        <CourseButtons />
                    </>
                );
            default:
                // fallback: show all buttons and charge notifier
                return (
                    <>
                        <ScheduleCourseChargeNotifier
                            organisationIsSubscribed={!paymentSetupRequired}
                            button={<ScheduleButton />}
                        />
                        <CourseIsLiveEditWarning />
                        <CourseButtons />
                    </>
                );
        }
    }

    return (
        <MainSection>
            <Container>
                {openSetupPaymentModal && (
                    <PaymentSetupModal
                        closeModal={closePaymentModal}
                        closeModalAfterAddCard={closePaymentModalAfterAddCard}
                    />
                )}

                <ButtonPanel />

                <HorizontalRule />

                {course.description && (
                    <FlexColMd>
                        <Headline3>{strings.publishedCoursePage.courseDescriptionHeader}</Headline3>
                        <CourseDescriptionCard>{course.description}</CourseDescriptionCard>
                    </FlexColMd>
                )}

                <FlexColMd>
                    <Headline3>{strings.publishedCoursePage.learningObjectivesHeader}</Headline3>
                    <LearningObjectiveContainer>
                        {course.learningObjectives
                            .sort((a, b) => a.order - b.order)
                            .map((x) => (
                                <LearningObjective key={x.id}>
                                    <LearningObjectiveIconContainer>
                                        <CheckIcon />
                                    </LearningObjectiveIconContainer>
                                    <BodyRegular>{x.description}</BodyRegular>
                                </LearningObjective>
                            ))}
                    </LearningObjectiveContainer>
                </FlexColMd>

                <FlexColMd>
                    <Headline3>{strings.publishedCoursePage.modulesHeader}</Headline3>
                    <FlexColRg>
                        {course.modules
                            .sort((moduleOne, moduleTwo) => {
                                return moduleOne.order < moduleTwo.order ? -1 : 1;
                            })
                            .map((module) => (
                                <ModuleAccordion key={module.id} module={module} />
                            ))}
                    </FlexColRg>
                </FlexColMd>
            </Container>

            {openCopyCourseModal && (
                <Alert
                    alertText={`${strings.courseOverview.copyCourseAlert.alertText} ${course.title}?`}
                    buttonText={strings.courseOverview.copyCourseAlert.copyButtonTitle}
                    ariaButtonText={strings.courseOverview.copyCourseAlert.copyButtonAria}
                    onSubmit={onCopy}
                    submitting={copyingCourse}
                    closeAlert={() => {
                        setOpenCopyCourseModal(false);
                    }}
                    error={
                        copyCourseError
                            ? {
                                  title: strings.courseOverview.copyCourseError.title,
                                  description: strings.courseOverview.copyCourseError.description,
                              }
                            : undefined
                    }
                />
            )}

            {openDeleteCourseAreYouSure && (
                <Alert
                    alertText={`${strings.courseOverview.deleteCourseAlert.alertText} ${course.title}?`}
                    buttonText={strings.courseOverview.deleteCourseAlert.deleteButtonTitle}
                    ariaButtonText={strings.courseOverview.deleteCourseAlert.deleteButtonAria}
                    onSubmit={onDelete}
                    submitting={deleteInProgress}
                    closeAlert={() => {
                        setOpenDeleteCourseAreYouSure(false);
                    }}
                    error={
                        deleteCourseError
                            ? {
                                  title: strings.courseOverview.deleteCourseError.title,
                                  description: strings.courseOverview.deleteCourseError.description,
                              }
                            : undefined
                    }
                />
            )}
        </MainSection>
    );
}

const Container = styled.main`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
    padding: ${sizes.spacingLg} 0;
`;

const CourseButtonsContainer = styled(FlexMdAlignCenter)`
    @media (max-width: ${breakpoints.lg}) {
        flex-direction: column;
        align-items: flex-start;
        max-width: 300px;
    }
    @media (max-width: ${breakpoints.sm}) {
        width: 100%;
    }
`;

const CourseDescriptionCard = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
`;

const LearningObjectiveContainer = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
    width: fit-content;
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const LearningObjective = styled(FlexAlignCenter)`
    gap: ${sizes.spacingMd};
`;

const LearningObjectiveIconContainer = styled.div`
    padding: ${sizes.spacingMd};
    height: 48px;
    width: 48px;
    border-radius: 50%;
    background-color: ${theme.notificationSuccessBackground};
`;
